import { X_CLIENT_TYPE } from '@alter/types';
import type { z } from 'zod';
import { HttpError, NetworkError, UnauthorizedError, ValidationError } from './errors.js';
import type { TokenStorage } from './tokenStorage.js';

export interface HttpClientConfig {
  /** Base URL del API REST, sin barra final. Ej.: `"https://api.mvp.alterrealitas.com"`. */
  restBaseUrl: string;
  /** Token storage para inyectar `Authorization: Bearer …` en requests autenticadas. */
  tokenStorage: TokenStorage;
  /** Override de `fetch` para tests. Default: `globalThis.fetch`. */
  fetch?: typeof fetch;
}

export interface RequestOptions {
  /**
   * Si `true`, NO se inyecta `Authorization` (ej. `/login`, donde aún no
   * hay token). Default: `false`.
   */
  unauthenticated?: boolean;
}

/**
 * Cliente HTTP fino con headers obligatorios, body JSON, validación Zod
 * de la respuesta y errores tipados. Pensado para ser consumido por los
 * clientes de dominio (`AuthClient`, `SimulacionClient`, `BitacoraClient`).
 */
export class HttpClient {
  private readonly fetchImpl: typeof fetch;

  constructor(private readonly config: HttpClientConfig) {
    this.fetchImpl = config.fetch ?? globalThis.fetch.bind(globalThis);
  }

  /** GET con validación Zod del body de respuesta. */
  async get<T>(path: string, schema: z.ZodSchema<T>, opts?: RequestOptions): Promise<T> {
    return this.request('GET', path, undefined, schema, opts);
  }

  /** POST con body JSON y validación Zod del body de respuesta. */
  async post<T>(
    path: string,
    body: unknown,
    schema: z.ZodSchema<T>,
    opts?: RequestOptions,
  ): Promise<T> {
    return this.request('POST', path, body, schema, opts);
  }

  private async request<T>(
    method: 'GET' | 'POST',
    path: string,
    body: unknown,
    schema: z.ZodSchema<T>,
    opts?: RequestOptions,
  ): Promise<T> {
    const url = `${this.config.restBaseUrl}${path}`;
    // Constante, no configurable: con cualquier otro valor cambia el claim
    // `tipoInterfaz` del JWT, y con él el formato de audio que el servidor
    // declara (nota de interfaz §2).
    const headers: Record<string, string> = {
      'X-Client-Type': X_CLIENT_TYPE,
    };
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
    if (!opts?.unauthenticated) {
      const token = await this.config.tokenStorage.get();
      if (token !== null) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const init: RequestInit = { method, headers };
    if (body !== undefined) {
      init.body = JSON.stringify(body);
    }

    let response: Response;
    try {
      response = await this.fetchImpl(url, init);
    } catch (cause) {
      throw new NetworkError(cause);
    }

    if (response.status === 401) {
      const errorBody = await safeReadText(response);
      throw new UnauthorizedError(errorBody);
    }

    if (!response.ok) {
      const errorBody = await safeReadText(response);
      throw new HttpError(response.status, response.statusText, errorBody);
    }

    let raw: unknown;
    try {
      raw = await response.json();
    } catch (cause) {
      throw new ValidationError(`Response is not valid JSON: ${String(cause)}`, undefined);
    }

    const parsed = schema.safeParse(raw);
    if (!parsed.success) {
      throw new ValidationError(parsed.error.issues, raw);
    }
    return parsed.data;
  }
}

async function safeReadText(response: Response): Promise<string | undefined> {
  try {
    return await response.text();
  } catch {
    return undefined;
  }
}
